/** @jsx jsx */

import type { ReactNode } from 'react';
import { Fragment, forwardRef } from 'react';
import { jsx } from '@balance-web/core';
import { useMaybeFieldContext } from '@balance-web/field';
import { DefaultTextPropsProvider, Text } from '@balance-web/text';
import { useTheme } from '@balance-web/theme';

import { RadioPrimitive } from './RadioPrimitive';
import { useRadioGroupContext } from './context';
import { getRadioGroupItemProps } from './useRadioGroupState';
import type { RadioProps } from './types';

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ children, ...consumerProps }, ref) => {
    const { spacing } = useTheme();
    const groupState = useRadioGroupContext();
    const fieldContext = useMaybeFieldContext();
    const invalid = fieldContext?.invalid ?? false;
    const inputProps = groupState
      ? getRadioGroupItemProps(consumerProps, groupState)
      : consumerProps;
    const isSmall = inputProps.size === 'small';

    return (
      <label
        css={{
          alignItems: 'flex-start',
          display: 'inline-flex',
          paddingBottom: invalid ? spacing.xsmall : undefined,
        }}
      >
        <RadioPrimitive ref={ref} {...inputProps} />
        <DefaultTextPropsProvider
          color={inputProps.disabled ? 'textDisabled' : undefined}
          leading={isSmall ? 'tight' : undefined}
          size={isSmall ? 'xsmall' : 'small'}
          weight="medium"
        >
          <div
            css={{
              marginLeft: spacing.small,
              paddingTop: spacing.xxsmall,
              userSelect: 'none',
            }}
          >
            <Content>{children}</Content>
          </div>
        </DefaultTextPropsProvider>
      </label>
    );
  }
);

const Content = ({ children }: { children: ReactNode }) => {
  // Support "naked" strings as children
  if (typeof children === 'string' || typeof children === 'number') {
    return <Text>{children}</Text>;
  }

  // NOTE: fragment resolves TS issue with component return types
  return <Fragment>{children}</Fragment>;
};
